import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {IDcpGlobalErrorMessage} from '@app/core/models/interfaces/error';
import {TemplateView} from '@app/model/template-view.model';
import {DcpGlobalMessageService, ValidationService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CartCouponComponent} from '@spartacus/cart/base/components';
import {voucherInvalidError} from '@spartacus/cart/base/core';
import {ActiveCartFacade, Cart, CartVoucherFacade} from '@spartacus/cart/base/root';
import {CustomerCouponService, ErrorModel} from '@spartacus/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {BadVoucherRequestHandler} from '@app/http-interceptors/handlers/dcp-bad-voucher-request.handler';
import {map, take, tap} from 'rxjs/operators';

@UntilDestroy() @Component({
  selector: 'dcp-cart-coupon',
  templateUrl: './dcp-cart-coupon.component.html',
  styleUrls: ['./dcp-cart-coupon.component.scss']
})

export class DcpCartCouponComponent extends CartCouponComponent implements OnInit, OnDestroy {
  @Input() selectedView: TemplateView;
  @Output() errorNoCoupon: EventEmitter<void> = new EventEmitter<void>();
  view = TemplateView;
  voucherError: ErrorModel;
  hideCouponField: boolean;
  showSpinner: boolean = false;
  couponLoading$ = this.cartVoucherFacade.getAddVoucherResultLoading();

  constructor(
    protected cartVoucherFacade: CartVoucherFacade,
    protected formBuilder: UntypedFormBuilder,
    protected customerCouponService: CustomerCouponService,
    protected activeCartFacade: ActiveCartFacade,
    protected dcpBadVoucherRequestHandler: BadVoucherRequestHandler,
    protected validationService: ValidationService,
    protected dcpGlobalMessageService: DcpGlobalMessageService,
  ) {
    super(cartVoucherFacade, formBuilder, customerCouponService, activeCartFacade);
  }

  ngOnInit() {
    this.cart$ = this.activeCartFacade.getActive().pipe(
      tap((cart: Cart) => {
        this.cartId = cart?.code;
        this.dcpGlobalMessageService.getActiveMessages().pipe(untilDestroyed(this), take(1)).subscribe(activeMessages => {
          this.showSpinner = false;
          activeMessages
            .filter(msgObj => this.getPromoRelatedPotentialPromotions(msgObj, cart))
            .forEach(msg => this.dcpGlobalMessageService.removeMessage(msg.id));
        });
        this.hideCouponField = cart.appliedProductPromotions?.some(promoResult => promoResult.hideCouponFieldWhenPromoApplied) ||
          cart.appliedOrderPromotions?.some(promoResult => promoResult.hideCouponFieldWhenPromoApplied);
      }),
      untilDestroyed(this)
    );

    this.cartIsLoading$ = this.activeCartFacade
      .isStable().pipe(map((loaded) => !loaded), untilDestroyed(this));

    this.cartVoucherFacade.resetAddVoucherProcessingState();

    this.couponForm = new FormGroup({
      couponCode: new FormControl<string>(''),
    });

    this.cartVoucherFacade.getAddVoucherResultSuccess().pipe(untilDestroyed(this)).subscribe(isAppliedSuccessfully => {
      if (isAppliedSuccessfully) {
        this.couponForm.get('couponCode').setValue('');
      }
    });

    this.dcpBadVoucherRequestHandler.voucherError.next({});

    this.couponForm.get('couponCode').setValidators(this.createCouponValidators());
    this.dcpBadVoucherRequestHandler.voucherError.pipe(untilDestroyed(this)).subscribe(voucherError => {
      this.voucherError = voucherError;
      this.couponForm.get('couponCode').updateValueAndValidity();
    });
  }

  applyVoucher(): void {
    this.showSpinner = true;
    this.resetCouponValidation();
    this.cartVoucherFacade.addVoucher(
      this.couponForm.value.couponCode,
      this.cartId
    );
  }

  hasValidationError(): boolean {
    return this.hasValidationFailed(this.couponForm.get('couponCode'), 'invalidCouponCode')
      || this.hasValidationFailed(this.couponForm.get('couponCode'), 'alreadyExistingCoupon')
      || this.hasValidationFailed(this.couponForm.get('couponCode'), 'cartFailedRequirements')
      || this.hasValidationFailed(this.couponForm.get('couponCode'), 'amountExceeded');
  }

  getErrorText(): string {
    if (this.hasValidationFailed(this.couponForm.get('couponCode'), 'invalidCouponCode'))
      return 'voucher.errors.invalidCouponCode';
    if (this.hasValidationFailed(this.couponForm.get('couponCode'), 'alreadyExistingCoupon'))
      return 'voucher.errors.alreadyExistingCoupon';
    if (this.hasValidationFailed(this.couponForm.get('couponCode'), 'cartFailedRequirements'))
      return 'voucher.errors.cartFailedRequirements';
    if (this.hasValidationFailed(this.couponForm.get('couponCode'), 'amountExceeded'))
      return 'voucher.errors.amountExceeded';
    return '';
  }

  hasValidationFailed(formControl: AbstractControl, validatorName: string) {
    return this.validationService.hasValidationFailed(formControl, validatorName, Boolean(this.voucherError?.message) || Boolean(this.voucherError?.type));
  }

  createCouponValidators(): ValidatorFn {
    return (): ValidationErrors | null => {
      if (voucherInvalidError(this.voucherError)) {
        return {invalidCouponCode: true};
      } else if (this.voucherAlreadyAppliedError(this.voucherError)) {
        return {alreadyExistingCoupon: true};
      } else if (this.isCartEntryError(this.voucherError)) {
        return {cartFailedRequirements: true};
      } else if (this.isAmountExceededError(this.voucherError)) {
        return {amountExceeded: true};
      }
      return null;
    };
  }

  onCouponChange(event: CustomEvent): void {
    const control = this.couponForm.get('couponCode');
    const target = event.target as HTMLInputElement;
    control.setValue(target.value);
  }

  voucherAlreadyAppliedError(error: ErrorModel): boolean {
    return error.message === 'coupon.already.exists.cart';
  }

  isAmountExceededError(error: ErrorModel): boolean {
    return error.message === 'You have entered too many voucher codes. Please try again later.';
  }

  isCartEntryError(error: ErrorModel): boolean {
    return error.type === 'InvalidEntryError' || error.message === 'coupon.invalid.cart.entry';
  }

  private resetCouponValidation() {
    this.voucherError = {};
    this.couponForm.get('couponCode').updateValueAndValidity();
  }

  private getPromoRelatedPotentialPromotions(msgObj: IDcpGlobalErrorMessage, cart: Cart) {
    return msgObj.group?.startsWith('potential') && cart.appliedVouchers?.some(voucher => msgObj?.id?.includes(voucher.code));
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onPromoErrorNoCouponCode() {
    this.errorNoCoupon.emit();
  }
}
